import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../Services/APIService';
import { RootState } from 'app/store';

// Interfaces (keeping all existing ones)
export interface UserInfo {
  id: string;
  name: string;
  email: string;
}

export interface AddPeoplePayload {
  group_id: string;
  service_id: string;
  user_info: Record<string, any>[];
}

export interface ListScreeningsPayload {
  group_id: string;
  employmentType?: string;
  service_id: string;
  page_size: number;
  next_token?: string | null;
  search?: string;
  status?: boolean;
  competency?: string;
  isDelete?: boolean;
}

export interface Screening {
  employeeId: string;
  isNewRecord: boolean;
  employeeName: string;
  status: boolean;
  competency: string;
  email: string;
  avatar: string;
  employmentType?: string;
  isDeleted?: boolean;
  isDelete?: boolean;
}

export interface ListScreeningsResponse {
  screenings: Screening[];
  next_token: string | null;
}

export interface EmployeeDetailsPayload {
  employeeId: string;
  details: Record<string, any>;
}

export interface FetchEmployeeScreeningPayload {
  group_id: string;
  service_id: string;
  email_key: string;
  id: string;
  isNewRecord: boolean;
  isDelete?: boolean;
}

export interface FetchEmployeeScreeningResponse {
  group_id: string;
  service_name_id: string;
  listing: Screening;
  details: Record<string, any>;
}

export interface DeleteEmployeePayload {
  group_id: string;
  service_id: string;
  email_key: string;
  employee_id: string;
}

export interface UpdateEmployeeStatusPayload {
  group_id: string;
  service_id: string;
  email_key: string;
  employee_id: string;
  status: boolean;
}

export interface UpdateEmployeeStatusResponse {
  message: string;
  employee_id: string;
  email: string;
  screening_status_updated: boolean;
  login_status_updated: boolean;
  old_screening_status: boolean;
  new_status: boolean;
  updated_at: string;
  updated_by: string;
}

// ✅ ADD: Correct interface for upload response
export interface UploadPeopleDetailsResponse {
  message: string;
  summary: {
    total_processed: number;
    successful_inserts: number;
    users_not_in_approved_members: number;
    users_already_registered: number;
    reactivated_employees: number;
    failed_employees: number;
  };
  details: {
    successful?: Array<{ user_info: any }>;
    users_not_in_approved_members?: Array<{ user_info: any }>;
    users_already_registered?: Array<{ user_info: any }>;
    reactivated?: Array<{ user_info: any }>;
    failed?: Array<{ user_info: any; reason: string }>;
  };
}

// ✅ ADD: Enhanced state to include upload results
export interface PeopleState {
  loading: boolean;
  error: string | null;
  success: boolean;
  message: string | null;
  screenings: Screening[];
  nextToken: string | null;
  loadingScreenings: boolean;
  screeningsError: string | null;
  hasMoreScreenings: boolean;
  employeeDetails: FetchEmployeeScreeningResponse | null;
  uploadResponse: UploadPeopleDetailsResponse | null; // ✅ ADD: Store upload response
  showUploadResults: boolean; // ✅ ADD: Control display
}

// ✅ UPDATED: Initial State
const initialState: PeopleState = {
  loading: false,
  error: null,
  success: false,
  message: null,
  screenings: [],
  nextToken: null,
  loadingScreenings: false,
  screeningsError: null,
  hasMoreScreenings: true,
  employeeDetails: null,
  uploadResponse: null, // ✅ ADD
  showUploadResults: false // ✅ ADD
};

// Thunks (keeping all existing ones)
export const addPeople = createAsyncThunk(
  'people/addPeople',
  async (payload: AddPeoplePayload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/people-screening/insert-employee', payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add people');
    }
  }
);

export const listScreenings = createAsyncThunk(
  'people/listScreenings',
  async (payload: ListScreeningsPayload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/people-screening/list-screenings', payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch screenings');
    }
  }
);

// ✅ FIXED: Correct return type for upload thunk
export const uploadPeopleDetailsFile = createAsyncThunk<
  UploadPeopleDetailsResponse, // ✅ FIXED: Use correct response type
  AddPeoplePayload,
  { state: RootState }
>('people/uploadPeopleDetailsFile', async (payload, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post(
      '/payrole/peoples/upload-peopleDetails-file',
      payload
    );
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to upload file');
  }
});

export const saveEmployeeDetails = createAsyncThunk(
  'people/saveEmployeeDetails',
  async (payload: EmployeeDetailsPayload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/people-screening/update-employee', payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to save employee details');
    }
  }
);

export const fetchEmployeeScreening = createAsyncThunk<
  FetchEmployeeScreeningResponse,
  FetchEmployeeScreeningPayload
>('people/fetchEmployeeScreening', async (payload, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post('/people-screening/fetch-emp-screening', payload);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch employee screening');
  }
});

export const deleteEmployee = createAsyncThunk<
  { message: string; employee_id: string },
  DeleteEmployeePayload,
  { state: RootState }
>('people/deleteEmployee', async (payload, { getState, rejectWithValue }) => {
  try {
    const response = await axiosInstance.post('/people-screening/delete-employee', payload);
    return {
      message: response.data.message || 'Employee deleted successfully',
      employee_id: payload.employee_id
    };
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to delete employee');
  }
});

export const updateEmployeeStatus = createAsyncThunk<
  UpdateEmployeeStatusResponse,
  UpdateEmployeeStatusPayload,
  { state: RootState }
>('people/updateEmployeeStatus', async (payload, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post('/people-screening/update-employee-status', payload);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.error || 'Failed to update employee status');
  }
});

// ✅ UPDATED: Slice with correct handling
const peopleSlice = createSlice({
  name: 'people',
  initialState,
  reducers: {
    resetPeopleState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.message = null;
      state.employeeDetails = null;
      state.uploadResponse = null; // ✅ ADD
      state.showUploadResults = false; // ✅ ADD
    },
    resetScreeningsState: (state) => {
      state.screenings = [];
      state.nextToken = null;
      state.loadingScreenings = false;
      state.screeningsError = null;
      state.hasMoreScreenings = true;
    },
    clearScreeningsError: (state) => {
      state.screeningsError = null;
    },
    // ✅ ADD: New actions for upload results
    clearUploadResults: (state) => {
      state.uploadResponse = null;
      state.showUploadResults = false;
    },
    hideUploadResults: (state) => {
      state.showUploadResults = false;
    }
  },
  extraReducers: (builder) => {
    builder
      // Add People
      .addCase(addPeople.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
        state.message = null;
      })
      .addCase(addPeople.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = action.payload?.message?.message || 'People added successfully';
      })
      .addCase(addPeople.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.success = false;
      })

      // List Screenings
      .addCase(listScreenings.pending, (state) => {
        state.loadingScreenings = true;
        state.screeningsError = null;
      })
      .addCase(listScreenings.fulfilled, (state, action) => {
        state.loadingScreenings = false;
        const { screenings, next_token } = action.payload as ListScreeningsResponse;
        if (action.meta.arg.next_token) {
          state.screenings = [...state.screenings, ...screenings];
        } else {
          state.screenings = screenings;
        }
        state.nextToken = next_token;
        state.hasMoreScreenings = next_token !== null;
      })
      .addCase(listScreenings.rejected, (state, action) => {
        state.loadingScreenings = false;
        state.screeningsError = action.payload as string;
      })

      // ✅ FIXED: Upload People Details File
      .addCase(uploadPeopleDetailsFile.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
        state.uploadResponse = null; // ✅ CLEAR previous results
        state.showUploadResults = false;
      })
      .addCase(uploadPeopleDetailsFile.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.uploadResponse = action.payload; // ✅ STORE entire response
        state.showUploadResults = true; // ✅ SHOW results

        // ✅ IMPROVED: Create meaningful message from response
        const { summary } = action.payload;
        const successCount = summary?.successful_inserts || 0;
        const totalCount = summary?.total_processed || 0;

        if (successCount > 0) {
          state.message = `Successfully imported ${successCount} out of ${totalCount} employees.`;
        } else {
          state.message = action.payload?.message || 'File upload completed';
        }
      })
      .addCase(uploadPeopleDetailsFile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.success = false;
        state.uploadResponse = null;
        state.showUploadResults = false;
      })

      // Save Employee Details (unchanged)
      .addCase(saveEmployeeDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(saveEmployeeDetails.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
        state.message = 'Employee details saved successfully';
      })
      .addCase(saveEmployeeDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.success = false;
      })

      // Fetch Employee Screening (unchanged)
      .addCase(fetchEmployeeScreening.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEmployeeScreening.fulfilled, (state, action) => {
        state.loading = false;
        state.employeeDetails = action.payload;
      })
      .addCase(fetchEmployeeScreening.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Delete Employee (unchanged)
      .addCase(deleteEmployee.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
        state.message = null;
      })
      .addCase(deleteEmployee.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = action.payload.message;
        state.screenings = state.screenings.filter(
          (screening) => screening.employeeId !== action.payload.employee_id
        );
      })
      .addCase(deleteEmployee.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.success = false;
      })

      // Update Employee Status (unchanged)
      .addCase(updateEmployeeStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
        state.message = null;
      })
      .addCase(updateEmployeeStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = action.payload.message;
        state.screenings = state.screenings.map((screening) =>
          screening.employeeId === action.payload.employee_id
            ? { ...screening, status: action.payload.new_status }
            : screening
        );
        if (
          state.employeeDetails &&
          state.employeeDetails.listing.employeeId === action.payload.employee_id
        ) {
          state.employeeDetails.listing.status = action.payload.new_status;
        }
      })
      .addCase(updateEmployeeStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.success = false;
      });
  }
});

// ✅ UPDATED: Export new actions
export const {
  resetPeopleState,
  resetScreeningsState,
  clearScreeningsError,
  clearUploadResults,
  hideUploadResults
} = peopleSlice.actions;

export default peopleSlice.reducer;
